import React, { useCallback } from 'react'
import { Box, Button, InputAdornment, TextField, Tooltip, Typography, IconButton, type TablePaginationProps } from '@mui/material'
import { StyledDataGrid } from '../../common/components'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { CountryApi, type CountryGridViewModel, LanguageApi, type LanguageDto, RegionApi, type RegionDto } from '../../common/services'
import {
  useQuery
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { CountryModal } from './components'
import AlertDelete from '../../common/components/AlertDelete'
import { type GridColDef, GridRowModes, type GridRowModesModel, type GridEventListener, GridRowEditStopReasons, type GridRowId, gridPageCountSelector, GridPagination, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { ToastContainer, toast } from 'react-toastify'
import KeyTranslation from '../../common/KeyTranslation.json'
import * as Yup from 'yup'
import MuiPagination from '@mui/material/Pagination'

export default function SystemDetail (): JSX.Element {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = React.useState(false)
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  )
  const { data, isFetching, refetch } = useQuery(['/api/v2/Country'], async () => {
    const { data } = await new CountryApi().apiV2CountryGet()
    return data
  }, { enabled: false })
  const language = useQuery(['/api/v2/Language'], async () => {
    const { data } = await new LanguageApi().apiV2LanguageGet()
    return data
  }, { enabled: false })
  const region = useQuery(['/api/v2/Region/GetAll'], async () => {
    const { data } = await new RegionApi().apiV2RegionGetAllGet()
    return data
  }, { enabled: false })
  React.useEffect(() => {
    void refetch()
    void language.refetch()
    void region.refetch()
  }, [])
  const [rows, setRows] = React.useState(data?.data)
  const [dialogOpen, setDialogOpen] = React.useState({
    open: false,
    id: 0
  })
  const handleDialogClose = useCallback(() => {
    setDialogOpen({
      open: false,
      id: 0
    })
  }, [])
  const handleDelete = async (id: number): Promise<void> => {
    try {
      const responese = await new CountryApi().apiV2CountryIdDelete(id)
      if (responese.status === 209) {
        handleDialogClose()
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        toast.error(t(`${responese.data.error}`))
      } else {
        await refetch()
        handleDialogClose()
        toast.success(t(KeyTranslation.Message_Delete_Success))
      }
    } catch (ex: any) {
      handleDialogClose()
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      toast.error(t(`${ex.response.data.error}`))
    }
  }

  const [countryModalProps, setCountryModalProps] = React.useState<{ visible: boolean, data?: CountryGridViewModel, isEditing: boolean }>({
    visible: false,
    data: undefined,
    isEditing: false
  })
  const [searchTerm, setSearchTerm] = React.useState('')
  const filteredData = React.useMemo(() => {
    if (data == null) {
      return []
    }

    if (searchTerm.length === 0) {
      return data.data
    }

    return data.data?.filter((country: CountryGridViewModel) => {
      const searchTermLower = searchTerm.toLowerCase()
      return ((country.code?.toLowerCase().includes(searchTermLower)) ?? false) ||
        country.name?.toLowerCase().includes(searchTermLower) ||
        ((country.displayName?.toLowerCase().includes(searchTermLower)) ?? false) ||
        ((country.regionName?.toLowerCase().includes(searchTermLower)) ?? false) ||
        country.languageName?.toLowerCase().includes(searchTermLower)
    })
  }, [data, searchTerm])
  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })

    const editedRow = rows?.find((row) => row.id === id)
    if (editedRow?.id != null) {
      setRows(rows?.filter((row) => row.id !== id))
    }
  }
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ): void => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }
  const handleRowModesModelChange = (
    newRowModesModel: GridRowModesModel
  ): void => {
    setRowModesModel(newRowModesModel)
  }
  const validationSchema = Yup.object({
    name: Yup.string().required(t(KeyTranslation.Message_CountryName_invalid) ?? ''),
    code: Yup.string().required(t(KeyTranslation.Message_CountryCode_invalid) ?? '')
  })
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const processRowUpdate = async (newRow: CountryGridViewModel) => {
    try {
      setIsLoading(true)
      await validationSchema.validate(newRow)
      const updatedRow = { ...newRow, isNew: false }
      setRows(rows?.map((row) => (row.id === newRow.id ? updatedRow : row)))
      const response = await new CountryApi().apiV2CountryPut(updatedRow)
      if (response.status === 209) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        toast.error(t(`${response.data.error}`))
      } else {
        toast.success(t(KeyTranslation.Message_Update_success))
      }
      return updatedRow
    } catch (ex: any) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      toast.error(t(`${ex.response.data.error}`))
    } finally {
      setIsLoading(false)
    }
  }
  const columnsDummy: Array<GridColDef<CountryGridViewModel>> = [
    {
      field: 'name',
      headerName: t(KeyTranslation.Header_CountryName) ?? '',
      flex: 1,
      editable: true
    },
    {
      field: 'code',
      headerName: t(KeyTranslation.Header_CountryCode) ?? '',
      flex: 1,
      editable: true
    },
    {
      field: 'displayName',
      headerName: t(KeyTranslation.Country_DisplayName) ?? '',
      flex: 1,
      editable: true
    },
    {
      field: 'languageName',
      headerName: t(KeyTranslation.Common_Language) ?? '',
      editable: true,
      flex: 1,
      type: 'singleSelect',
      valueOptions: language.data?.data?.map(
        (language: LanguageDto) => ({
          label: language.name,
          value: language.name,
          key: language.id
        })
      )
    },
    {
      field: 'regionName',
      headerName: t(KeyTranslation.Header_RegionName) ?? '',
      editable: true,
      flex: 1,
      type: 'singleSelect',
      valueOptions: region.data?.data?.map(
        (region: RegionDto) => ({
          label: region.name,
          value: region.name,
          key: region.id
        })
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit
        if (isInEditMode) {
          return [
            <Tooltip title={t(KeyTranslation.Common_Save)} key={id}>
              <IconButton onClick={handleSaveClick(id)} >
                <SaveOutlinedIcon/>
              </IconButton>
            </Tooltip>,
            <Tooltip title={t(KeyTranslation.Common_Cancel)} key={id}>
              <IconButton onClick={handleCancelClick(id)} >
                <CloseOutlinedIcon/>
              </IconButton>
            </Tooltip>
          ]
        }

        return [
          <><Tooltip title={t(KeyTranslation.Btn_Edit)} key={`${id}-edit`}>
          <IconButton onClick={handleEditClick(id)}>
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t(KeyTranslation.Btn_Delete)} key={`${id}-delete`}>
              <IconButton onClick={() => { setDialogOpen({ open: true, id: Number(id) }) }}>
                <DeleteOutlinedIcon/>
              </IconButton>
        </Tooltip></>
        ]
      }
    }
  ]
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function Pagination ({
    page,
    onPageChange,
    className
  }: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
    const apiRef = useGridApiContext()
    const pageCount = useGridSelector(apiRef, gridPageCountSelector)

    return (
      <MuiPagination
        color="primary"
        className={className}
        count={pageCount}
        page={page + 1}
        onChange={(event, newPage) => {
          onPageChange(event as any, newPage - 1)
        }}
        showFirstButton
        showLastButton
      />
    )
  }
  function CustomPagination (props: any): JSX.Element {
    return <GridPagination ActionsComponent={Pagination} {...props} />
  }
  return (
    <Box>
      <Typography variant='h1'>{t(KeyTranslation.Common_Country)}</Typography>
      <Box sx={{ mt: 5 }} className="main">
        <Box sx={{ mb: 5 }} display="flex" justifyContent="space-between">
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              )
            }}
            variant="outlined"
            placeholder={t(KeyTranslation.Common_Search) ?? ''}
            size='small'
            value={searchTerm}
            onChange={(event) => { setSearchTerm(event.target.value) }}
          />
          <Button variant='contained' onClick={() => {
            setCountryModalProps({
              visible: true,
              data: undefined,
              isEditing: false
            })
          }} >{t(KeyTranslation.Country_New_Country)}</Button>
        </Box>
        <StyledDataGrid
          autoHeight
          loading={isLoading || isFetching}
          rows={filteredData ?? []}
          columns={columnsDummy}
          rowSelection={false}
          disableColumnMenu={true}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          slots={{
            pagination: CustomPagination
          }}
        />
        {countryModalProps.visible &&
          <CountryModal open={countryModalProps.visible} onClose={(): void => {
            void refetch()
            setCountryModalProps({
              visible: false,
              data: undefined,
              isEditing: false
            })
          }} />
        }
      <AlertDelete handleDelete={() => { void handleDelete(dialogOpen.id) }} dialogOpen={dialogOpen.open} handleDialogClose={handleDialogClose} />
      <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Box>
    </Box>
  )
}
